import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL || 'http://' + window.location.hostname + ':8000'}/api`;
const TOKEN_KEY = 'token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((t) => {
    if (t) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    applyToken(null);
    setLoading(false);
  }, [applyToken]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return true;
    } catch (error) {
      const status = error?.response?.status;
      console.error('fetchUser failed:', status, error?.response?.data || error.message);

      // Sadece gerçekten token geçersizse logout
      if (status === 401) {
        logout();
      } else {
        setLoading(false);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    applyToken(token);

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, applyToken, fetchUser]);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token: newToken, user: newUser } = response.data || {};

      if (!newToken) {
        throw new Error('Login response token missing');
      }

      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setUser(newUser || null);
      applyToken(newToken);

      // fetchUser kaldırıldı; login response'daki user yeterli
      return true;
    } catch (error) {
      console.error('login failed:', error?.response?.status, error?.response?.data || error.message);
      logout();
      throw error;
    } finally {
      setLoading(false);
    }
  }, [applyToken, logout]);

  const previewLogin = useCallback((role) => {
    if (!['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      return false;
    }

    setToken(null);
    localStorage.removeItem(TOKEN_KEY);
    applyToken(null);
    setUser({
      id: `local-preview-${role}`,
      username: `onizleme.${role}`,
      full_name: role === 'security' ? 'Güvenlik Önizleme' : 'Admin Önizleme',
      role,
    });
    setLoading(false);
    return true;
  }, [applyToken]);

  const value = useMemo(
    () => ({ user, token, login, previewLogin, logout, loading }),
    [user, token, login, previewLogin, logout, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
